import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Img } from 'react-image';
import * as Yup from 'yup';
import { FiPlay, FiSearch } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format } from 'date-fns';
// servicos
import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import notfound from 'assets/semimagem.png';
import NoSearch from 'assets/nosearch.svg';
import { show } from 'store/modules/dialog/actions';
import AppError from 'utils/AppError';

import Spinner from 'components/Spinner';
import Card from 'components/Card';
import PlayerBaixa from 'components/PlayerBaixa';
import Pagination from 'components/Pagination';
import Input from 'components/Input';
import NotFound from 'components/NotFound';
import Loader from 'components/ContentLoader/ORALoader';
import { Comercial, Legenda } from 'pages/@types';

import { Table, ThumbnailContainer } from 'styles/global';
import { Header, Scroll } from '../styles';
// configuração do validator
const schema = Yup.object().shape({
  filtro: Yup.string()
    .required('Campo Obrigatório')
    .min(3, 'Mínimo de 3 caracteres'),
});

interface BuscaComercialProps {
  palavraChaveSearch?: string;
}

export default function BuscaComercial({
  palavraChaveSearch = '', // Recebe a Prop e define um valor padrão, sem chance de ser undefined
}: BuscaComercialProps) {
  /**
   * USE STATE
   */
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });
  const [loading, setLoading] = useState(false);
  const [comerciais, setComerciais] = useState<Comercial[]>([]);
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [pages, setPages] = useState(1);
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  /**
   * USE REF
   */
  const ref = useRef<FormHandles>(null);
  /**
   * USE CALLBACK
   */
  const handlePlayer = useCallback(
    (previewurl: string) => {
      if (previewurl) {
        setPlayer({
          open: !player.open,
          midia_url: previewurl,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );
  function BoldedText(text: string, shouldBeBold: string) {
    const normalizedBold = shouldBeBold
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const textArray = text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(RegExp(normalizedBold, 'ig'));
    const match = text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(RegExp(normalizedBold, 'ig'));

    return (
      <p>
        {textArray.map((item, index) => {
          return (
            <Fragment key={`legi_${index}`}>
              {item}
              {index !== textArray.length - 1 && match && (
                <b>
                  <u>{shouldBeBold}</u>
                </b>
              )}
            </Fragment>
          );
        })}
      </p>
    );
  }
  const getTranscricao = useCallback(
    (legendas?: Legenda[]) => {
      if (legendas) {
        const regexSearch = RegExp(
          // Remove os acentos (accent/diacritics) da palavra chave
          filtro.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
          'ig',
        );
        // Busca em toda transcrição as linhas que têm a palavra chave
        const legendaMatch = legendas.find(l => {
          return (
            (l.linha1 &&
              l.linha1
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .search(regexSearch) >= 0) ||
            (l.linha2 &&
              l.linha2
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .search(regexSearch) >= 0)
          );
        });
        return (
          <Fragment key={`leg_${legendaMatch?.id}`}>
            {BoldedText(legendaMatch?.linha1 || '', filtro)}
            {BoldedText(legendaMatch?.linha2 || '', filtro)}
          </Fragment>
        );
      }
    },
    [filtro],
  );
  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const handleSubmit = useCallback(async data => {
    try {
      if (ref.current) {
        ref.current.setErrors({});
        await schema.validate(data, { abortEarly: false });

        setFiltro(data.filtro);
        setPage(1);
      }
    } catch (err) {
      console.log(err);
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        if (ref.current) ref.current.setErrors(errors);
        toast.error(err.message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        toast.error(appError.message);
      }
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      ref.current.setFieldValue('filtro', palavraChaveSearch); // Altera o valor visual do campo do formulário
      setFiltro(palavraChaveSearch);
      setPage(1); // Volta para a primeira página
    }
  }, [palavraChaveSearch]);
  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function buscaComerciais() {
      try {
        setLoading(true);
        if (filtro) {
          const response = await api.get('/ora/buscacomercial', {
            signal: controller.signal,
            params: {
              search: filtro,
              page: page,
            },
          });
          if (response && response.data) {
            setComerciais(response.data.comerciais);
            setPages(response.data.pages);
          }
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    buscaComerciais();
    return () => {
      controller.abort();
    };
  }, [page, filtro]);

  return (
    <div>
      <Form ref={ref} onSubmit={handleSubmit}>
        <Input
          name="filtro"
          placeholder="Buscar comerciais"
          autoComplete="off"
          icon={<FiSearch />}
        />
      </Form>
      <Card>
        <Scroll $height="calc(100vh - 225px)">
          <Header>
            <h4>LISTA DE COMERCIAIS</h4>
            <Pagination
              total={pages}
              activePage={page}
              onClick={page => setPage(page)}
            />
          </Header>
          {loading ? (
            <Loader />
          ) : filtro.length >= 3 && comerciais && comerciais.length === 0 ? (
            <div
              style={{
                width: '100%',
                position: 'absolute',
              }}
            >
              <NotFound img={NoSearch} title="Nenhum comercial">
                encontrado por{' '}
                <b>
                  <u>{filtro}</u>
                </b>
              </NotFound>
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th style={{ paddingLeft: '1rem', width: '230px' }}>
                    PREVIEW
                  </th>
                  <th>DADOS</th>
                  <th>EMISSORAS</th>
                  <th>DATA</th>
                  <th>TRANSCRIÇÃO</th>
                </tr>
              </thead>
              <tbody>
                {comerciais &&
                  comerciais.length > 0 &&
                  comerciais.map(c => (
                    <tr key={c.id}>
                      <td>
                        <ThumbnailContainer
                          onClick={() =>
                            handlePlayer(c.midia?.preview_url || '')
                          }
                        >
                          <Img
                            key={`img${c.id}`}
                            onClick={() =>
                              handlePlayer(c.midia?.preview_url || '')
                            }
                            src={c.midia?.thumbnail_url || ''}
                            loader={<Spinner />}
                            unloader={<img src={notfound} alt="sem thumb" />}
                          />

                          <FiPlay className="icon_play" />
                        </ThumbnailContainer>
                      </td>
                      <td>
                        <div>
                          <p>CM: {c.codmaterial}</p>
                          <p>TÍTULO: {c.titulo}</p>
                          <p>CLIENTE: {c.cliente.mnemonico}</p>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p>PONTO DE VENDA: {c.ptovenda}</p>
                          <p>
                            EXIBIDORAS:{' '}
                            {new Set(c.enviaexibidor).size === 8
                              ? 'REDE'
                              : Array.from(
                                  new Set(
                                    c.enviaexibidor.map(e => e.emissora.sigla),
                                  ),
                                ).join()}
                          </p>
                        </div>
                      </td>
                      <td>
                        <p>
                          {c.midia?.provided_at
                            ? format(new Date(c.midia.provided_at), 'dd/MM/yy')
                            : 'Indefinida'}
                        </p>
                      </td>
                      <td>{getTranscricao(c.midia?.legenda)}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          )}
        </Scroll>
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </div>
  );
}
